import { useCallback, useEffect, useRef } from 'react';
import { markWideMath } from './wideMath';
import { keepPunctuationWithMath } from './mathPunctuation';

// Helper function to ensure MathJax is ready and loaded
export const waitForMathJax = () => {
  return new Promise((resolve) => {
    if (window.MathJax && window.MathJax.typesetPromise) {
      if (window.MathJax.startup && window.MathJax.startup.promise) {
        window.MathJax.startup.promise.then(() => resolve()).catch(() => resolve());
      } else {
        resolve();
      }
      return;
    }

    if (!window.MathJax) {
      window.MathJax = {
        tex: {
          inlineMath: [['$', '$'], ['\\(', '\\)']],
          displayMath: [['$$', '$$'], ['\\[', '\\]']],
          processEscapes: true,
          processEnvironments: true
        },
        options: {
          skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code'],
          ignoreHtmlClass: 'tex2jax_ignore',
          processHtmlClass: 'tex2jax_process|mathjax-process'
        },
        startup: {
          typeset: false
        }
      };
    }

    if (!document.getElementById('MathJax-script')) {
      const script = document.createElement('script');
      script.id = 'MathJax-script';
      script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js';
      script.async = true;
      script.onload = () => {
        if (window.MathJax?.startup?.promise) {
          window.MathJax.startup.promise.then(() => resolve()).catch(() => resolve());
        } else {
          resolve();
        }
      };
      script.onerror = () => resolve();
      document.head.appendChild(script);
    } else {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (window.MathJax?.typesetPromise || attempts > 50) {
          clearInterval(interval);
          resolve();
        }
      }, 60);
    }
  });
};

/**
 * Typesets the maths inside `ref` whenever `deps` change, as the paper player
 * does: once now and again at 100 and 350 ms (content painted late), each
 * after MathJax has started up. A run superseded by a newer one, or already
 * typeset, does nothing. MathContent only draws HTML; this is what turns its
 * LaTeX into maths. A formula too wide for its line then scrolls sideways
 * (markWideMath), and punctuation after a formula stays on its line
 * (keepPunctuationWithMath).
 */
export function useMathJaxTypeset(ref, deps) {
  const runIdRef = useRef(0);
  const doneRunIdRef = useRef(-1);
  const typeset = useCallback(async (runId) => {
    if (!window.MathJax) return;
    await waitForMathJax();
    if (window.MathJax.startup && window.MathJax.startup.promise) {
      try { await window.MathJax.startup.promise; } catch (e) {}
    }
    if (runId !== runIdRef.current) return; // a newer run has started
    if (doneRunIdRef.current >= runId) return; // this run already typeset
    if (!window.MathJax || !window.MathJax.typesetPromise) return;
    const node = ref.current;
    if (!node) return;
    try {
      if (window.MathJax.typesetClear) {
        try { window.MathJax.typesetClear([node]); } catch (e) {}
      }
      await window.MathJax.typesetPromise([node]);
      keepPunctuationWithMath(node);
      markWideMath(node);
      doneRunIdRef.current = runId;
    } catch (error) {
      console.warn('MathJax typeset notice:', error);
    }
  }, [ref]);

  useEffect(() => {
    const runId = ++runIdRef.current;
    typeset(runId);
    const timeout1 = setTimeout(() => typeset(runId), 100);
    const timeout2 = setTimeout(() => typeset(runId), 350);
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
    };
  }, [typeset, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
}
